import { setContext } from '@sentry/core';
// eslint-disable-next-line no-restricted-imports
import { format } from 'date-fns';

import { parseMobileApp } from 'hooks/useIsMobileApp';
import { sessionStorage } from 'lib/storage/sessionStorage';
import { randomUUID } from 'lib/uuid';

import config from './config.json';

type Environment = 'staging' | 'development' | 'production' | 'mockprod';

// eslint-disable-next-line no-restricted-properties
const { origin, protocol, hostname } = window.location ?? {
  hostname: '',
  origin: '',
};

export const ORIGIN = origin;
export const HOSTNAME = hostname;

const mobileApp = sessionStorage.getItem('mobileApp');
setContext('mobileApp', { ...parseMobileApp(mobileApp), mobileApp });

const PREVIEW_RE = /preview\.sorare\.(?=(dev|tech)$)/;

const getEnvConfig = () => {
  if (import.meta.env.STORYBOOK === 'true') {
    // Always use local env in storybook
    return {
      ...config.development,
      // Force HTTPS to fix HTTPS enabled storybook in Safari
      backendHost: `https://${new URL(config.development.backendHost).host}`,
    };
  }
  if (hostname.match(PREVIEW_RE)) {
    return {
      ...(hostname.endsWith('.tech') ? config.mockprod : config.staging),
      walletUrl: ORIGIN.replace(PREVIEW_RE, 'preview.wallet.sorare.'),
    };
  }
  if (hostname.match(/sorare.dev/)) {
    return config.staging;
  }
  if (hostname.match(/sorare.tech/)) {
    return config.mockprod;
  }
  if (hostname.match(/sorare.co$/)) {
    return {
      ...config.production,
      backendHost: config.production.backendHost.replace(
        'sorare.com',
        'sorare.co'
      ),
      walletUrl: config.production.walletUrl.replace('sorare.com', 'sorare.co'),
      wsUrl: config.production.wsUrl.replace('sorare.com', 'sorare.co'),
    };
  }
  if (hostname === 'frontend') {
    // this case is used when taking card snapshot in the dev environment
    return {
      ...config.development,
      backendHost: 'http://backend:3000',
    };
  }
  if (hostname.match(/.*\.local/)) {
    return {
      ...config.development,
      backendHost: `${protocol}//${hostname}:3000`,
      walletUrl: `${protocol}//${hostname}:3002`,
    };
  }

  if (hostname.match(/localhost|(192\.168\..*\..*)|(10\.0\.2\.2)/)) {
    const { port } = new URL(config.development.walletUrl);
    return {
      ...config.development,
      walletUrl: `${protocol}//${hostname}:${port}`,
    };
  }

  return config.production;
};

let envConfig = getEnvConfig();
export const sessionForcedEnv: Environment | null = import.meta.env.DEV
  ? (sessionStorage.getItem('forcedEnv') as Environment | null)
  : null;

const sessionForcedFlags: Environment | null = sessionStorage.getItem(
  'forcedFlags'
) as Environment;
export const ACTUAL_ENV = envConfig.env;

if (sessionForcedEnv && config[sessionForcedEnv]) {
  envConfig = {
    ...config[sessionForcedEnv],
    backendHost: `${origin}/${sessionForcedEnv}/api`,
    wsUrl: `${protocol === 'https:' ? 'wss' : 'ws'}://${HOSTNAME}:3003/${sessionForcedEnv}/ws`,
    launchDarklyClientSideID: envConfig.launchDarklyClientSideID,
    walletUrl: envConfig.walletUrl,
  };
}

if (
  ACTUAL_ENV !== 'production' &&
  sessionForcedFlags &&
  config[sessionForcedFlags]
) {
  envConfig = {
    ...envConfig,
    launchDarklyClientSideID:
      config[sessionForcedFlags].launchDarklyClientSideID,
  };
}

export const isForcedEnv = () => {
  return !!sessionStorage.getItem('forcedEnv');
};

export const ENV = envConfig.env;
export const isStaging = ENV === 'staging';
export const isMockprod = ENV === 'mockprod';
export const isProduction = ENV === 'production';

export const API_ROOT = envConfig.backendHost;
const API_PATH = '/graphql';
export const API_URL = `${API_ROOT}${API_PATH}`;
export const STRIPE_PUBLIC_KEY = envConfig.stripePublicKey;
export const WALLET_URL = envConfig.walletUrl;
export const ETHERSCAN_URL =
  envConfig.env === 'production'
    ? 'https://etherscan.io'
    : 'https://sepolia.etherscan.io';
export const SOLANA_NET =
  envConfig.env === 'production' ? ('mainnet' as const) : ('devnet' as const);
export const RAMP_API_KEY = envConfig.rampApiKey;
export const RAMP_URL = envConfig.rampUrl;
export const MOONPAY_URL = envConfig.moonpayUrl;
export const LAUNCH_DARKLY_CLIENT_SIDE_ID = envConfig.launchDarklyClientSideID;
export const SEGMENT_API_KEY = envConfig.segmentApiKey;
export const AMPLITUDE_API_KEY = envConfig.amplitudeApiKey;
export const IMPACT_PAGE_LOAD_API_URL = envConfig.impactPageLoadAPIUrl;
export const WALLET_CONNECT_PROJECT_ID = 'b1e8df3bb981e9917bfeeb0ad4110bb7';
export const MANGOPAY_CLIENT_ID = envConfig.mangopayClientId;
export const NETHONE_PROFILER_URL = envConfig.nethoneProviderUrl;
export const GOOGLE_MAPS_API_KEY = envConfig.googleMapsApiKey;
export const ASSETS_HOST = envConfig.assetsHost;

const CLIENT_TYPE = 'Web';
const { sorareRevision } = document.documentElement.dataset;
export const REVISION = sorareRevision!;

export const IS_PLAYGROUND = process.env.PLAYGROUND === 'true';
// https://docs.prerender.io/docs/33-overview-of-prerender-crawlers
export const IS_PRERENDER =
  navigator.userAgent.includes(
    'Prerender (+https://github.com/prerender/prerender)'
  ) ||
  //https://gitlab.com/sorare/infra/-/merge_requests/2327
  navigator.userAgent === 'Sorare Headless Chromium';
export const IS_TEST_RUNNER =
  import.meta.env.NODE_ENV === 'test' || import.meta.env.STORYBOOK === 'true';

function makeTabVersion() {
  // Generate random characters in case multiple tabs are opened at the exact same time
  const rand = randomUUID().substring(0, 4);
  // data-sorare-fetched-at is injected by the Cloudflare worker
  const fetchedAt = document.documentElement.dataset.sorareFetchedAt;
  const isDateValid = (date: Date) => !Number.isNaN(date.getTime());
  const isFetchedAtValid = fetchedAt && isDateValid(new Date(fetchedAt));
  const date = format(
    isFetchedAtValid ? new Date(fetchedAt) : new Date(),
    'yyyyMMddHHmmss'
  );
  // Append 'z' to differentiate whether the version comes from fetched at
  return `${rand}${date}${isFetchedAtValid ? 'z' : ''}`;
}
const TAB_VERSION = makeTabVersion();
export const VERSION =
  REVISION === 'development' || import.meta.env.MODE === 'development'
    ? new Date().toISOString().replace(/[-T:]/g, '').slice(0, 14)
    : Number(document.documentElement.dataset.sorareVersion);

export const CLIENT_HEADERS: Record<string, string> = {
  'sorare-client': CLIENT_TYPE,
  'sorare-version': VERSION.toString(),
  'sorare-build': REVISION,
  'sorare-tab-version': TAB_VERSION,
};

// The WebSocket API doesn't support custom headers so we put them in the URL
export const WS_ROOT = `${envConfig.wsUrl}/cable?${new URLSearchParams(CLIENT_HEADERS).toString()}`;

export const SO5_SENTRY_DSN =
  'https://6aa84363323647f78159a04b60f11ffe@o1067581.ingest.sentry.io/6105154';

export const SHARING_ORIGIN = envConfig.origin || ORIGIN;

export const GOOGLE_OAUTH_CLIENT_ID =
  '140311826455-9ov80ptmkpa210h04on3uig3kg9st156.apps.googleusercontent.com';

export const SIGNUP_AGE_LIMIT = '16';
export const DEBUG_I18N = sessionStorage.getItem('debugI18n') === 'true';
